import { useLanguage } from '../LanguageContext';
import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, LinearProgress, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';

const normalize = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const valueOf = (row, column) => column.value ? column.value(row) : row[column.key];

export function selectRows(rows, columns, search, filters, sort) {
  const searchable = columns.filter(column => column.searchable !== false);
  const terms = normalize(search).split(/\s+/).filter(Boolean);
  const result = rows.filter(row =>
    terms.every(term => searchable.some(column => normalize(valueOf(row, column)).includes(term))) &&
    searchable.every(column => !filters[column.key] || normalize(valueOf(row, column)).includes(normalize(filters[column.key])))
  );
  const column = columns.find(item => item.key === sort.key);
  if (column) result.sort((a, b) => {
    const left = column.sortValue ? column.sortValue(a) : valueOf(a, column);
    const right = column.sortValue ? column.sortValue(b) : valueOf(b, column);
    const comparison = typeof left === 'number' && typeof right === 'number'
      ? left - right : String(left ?? '').localeCompare(String(right ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    return sort.direction === 'asc' ? comparison : -comparison;
  });
  return result;
}

export default function DataTable({ rows, columns, rowKey, label, loading = false, emptyMessage = 'No records found.', defaultSort = {} }) {
  const { t, locale } = useLanguage();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [hidden, setHidden] = useState([]);
  const [menu, setMenu] = useState(null);
  const [compact, setCompact] = useState(false);
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const visible = columns.filter(column => !hidden.includes(column.key));
  const filtered = selectRows(rows, columns, search, filters, sort);
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / size) - 1));
  useEffect(() => { setPage(safePage); }, [safePage]);
  const activeFilters = Object.values(filters).filter(value => normalize(value)).length;
  const clear = () => { setSearch(''); setFilters({}); setPage(0); };

  return <Paper className="data-panel" variant="outlined">
    <Box className="table-toolbar">
      <TextField size="small" label={t("Search {label}", { label: t(label) })} placeholder={t("Name, ID, or any column…")} value={search}
        onChange={event => { setSearch(event.target.value); setPage(0); }} sx={{ flex: '1 1 260px' }} />
      <Button variant={showFilters ? 'contained' : 'outlined'} aria-pressed={showFilters} onClick={() => setShowFilters(!showFilters)}>{t("Filters")}{activeFilters ? ` (${activeFilters})` : ''}</Button>
      <Button variant="outlined" aria-haspopup="menu" aria-expanded={Boolean(menu)} onClick={event => setMenu(event.currentTarget)}>{t("Columns")}</Button>
      <Button variant="outlined" aria-pressed={compact} onClick={() => setCompact(!compact)}>{compact ? t("Comfortable rows") : t("Compact rows")}</Button>
      {(search || activeFilters > 0) && <Button onClick={clear}>{t("Clear search & filters")}</Button>}
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {columns.filter(column => column.searchable !== false).map(column => <MenuItem key={column.key}>
          <FormControlLabel label={t(column.label)} control={<Checkbox checked={!hidden.includes(column.key)}
            disabled={!hidden.includes(column.key) && visible.filter(item => item.searchable !== false).length === 1}
            onChange={event => setHidden(event.target.checked ? hidden.filter(key => key !== column.key) : [...hidden, column.key])} />}/>
        </MenuItem>)}
      </Menu>
    </Box>
    <Box className="table-summary"><Typography variant="body2" role="status">{t("{count} of {total} records", { count: filtered.length.toLocaleString(locale), total: rows.length.toLocaleString(locale) })}</Typography>
      <Typography variant="caption">{t("Search includes hidden columns · Click a heading to sort")}</Typography></Box>
    {showFilters && <Box className="column-filters">
      {columns.filter(column => column.searchable !== false).map(column => <TextField key={column.key} size="small" label={t("Filter {label}", { label: t(column.label) })} value={filters[column.key] || ''}
        onChange={event => { setFilters({ ...filters, [column.key]: event.target.value }); setPage(0); }} />)}
    </Box>}
    {loading && <LinearProgress aria-label={t("Loading {label}", { label: t(label) })} />}
    <TableContainer tabIndex={0} aria-label={t("{label} table scroll area", { label: t(label) })}>
      <Table size={compact ? 'small' : 'medium'} aria-label={t(label)} aria-busy={loading}>
        <TableHead><TableRow>{visible.map(column => <TableCell key={column.key} sortDirection={sort.key === column.key ? sort.direction : false}>
          {column.searchable === false ? column.label : <TableSortLabel active={sort.key === column.key} direction={sort.key === column.key ? sort.direction : 'asc'}
            onClick={() => { setSort({ key: column.key, direction: sort.key === column.key && sort.direction === 'asc' ? 'desc' : 'asc' }); setPage(0); }}>{t(column.label)}</TableSortLabel>}
        </TableCell>)}</TableRow></TableHead>
        <TableBody>{filtered.slice(safePage * size, (safePage + 1) * size).map(row => <TableRow hover key={rowKey(row)}>
          {visible.map(column => <TableCell key={column.key} className={column.mono ? 'identity-cell' : undefined}>
            {column.render ? column.render(row) : (valueOf(row, column) ?? '—')}
          </TableCell>)}
        </TableRow>)}
        {!filtered.length && <TableRow><TableCell colSpan={visible.length} className="empty-state">{loading ? t("Loading records…") : search || activeFilters ? t("No matches. Try a different name or ID, or clear your filters.") : t(emptyMessage)}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination component="div" count={filtered.length} page={safePage} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50, 100]}
      onPageChange={(_, next) => setPage(next)} onRowsPerPageChange={event => { setSize(Number(event.target.value)); setPage(0); }} showFirstButton showLastButton />
  </Paper>;
}
